import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldAlert, ArrowLeft, UserCog, UserPlus, Mail, Trash2, Pencil, Loader2, X, KeyRound,
} from "lucide-react";

const empty = { first_name: "", last_name: "", email: "", password: "" };

const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState("");

  const load = async () => {
    try { setManagers(await dashApi.managers()); }
    catch { toast.error("Impossible de charger les managers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => { setForm(empty); setEditId(null); };

  const startEdit = (m) => {
    setEditId(m.id);
    setForm({ first_name: m.first_name || "", last_name: m.last_name || "", email: m.email || "", password: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) return toast.error("Nom, prénom et email obligatoires");
    if (!editId && !form.password) return toast.error("Mot de passe requis");
    setSaving(true);
    try {
      if (editId) { await dashApi.updateManager(editId, form); toast.success("Manager mis à jour"); }
      else { await dashApi.createManager(form); toast.success(`Manager ${form.first_name} ${form.last_name} créé`); }
      reset(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    setRemoving(m.id);
    try { await dashApi.removeManager(m.id); toast.success("Manager supprimé"); setManagers((p) => p.filter((x) => x.id !== m.id)); if (editId === m.id) reset(); }
    catch { toast.error("Échec de la suppression"); }
    finally { setRemoving(""); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><UserCog className="w-4 h-4 text-yellow-400" /> Panneau Administrateur</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Gestion des comptes Managers</p>
          </div>
        </div>
        <Button data-testid="admin-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <section className="lg:col-span-2">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 sticky top-24">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                {editId ? <Pencil className="w-5 h-5 text-yellow-400" /> : <UserPlus className="w-5 h-5 text-emerald-400" />}
                {editId ? "Modifier le manager" : "Créer un manager"}
              </h2>
              {editId && <button data-testid="manager-cancel-edit" onClick={reset} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button>}
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Prénom</Label><Input data-testid="manager-firstname-input" value={form.first_name} onChange={set("first_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Nom</Label><Input data-testid="manager-lastname-input" value={form.last_name} onChange={set("last_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Email</Label><Input data-testid="manager-email-input" type="email" value={form.email} onChange={set("email")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="manager@samajaamu.sn" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-wider text-slate-400">{editId ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label><Input data-testid="manager-password-input" type="text" value={form.password} onChange={set("password")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder={editId ? "Laisser vide pour conserver" : "••••••••"} /></div>
              <Button type="submit" data-testid="manager-save-btn" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Enregistrer" : "Créer le manager")}
              </Button>
            </form>
            <div className="mt-5 pt-4 border-t border-slate-800 text-xs text-slate-500 flex items-start gap-2">
              <KeyRound className="w-3.5 h-3.5 mt-0.5 shrink-0 text-yellow-400" />
              Connecté en tant que <strong className="text-slate-300">{user?.name}</strong> (admin). Les managers se connectent avec leur email + mot de passe.
            </div>
          </div>
        </section>

        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg">Managers</h2>
            <span className="font-mono text-xs text-slate-500" data-testid="managers-count">{managers.length} manager(s)</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : managers.length === 0 ? (
            <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
              <UserCog className="w-8 h-8 mx-auto mb-3 text-slate-600" />
              <p className="font-display font-semibold text-slate-300">Aucun manager</p>
              <p className="text-sm mt-1">Créez un compte manager via le formulaire.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="managers-list">
              {managers.map((m) => (
                <div key={m.id} data-testid={`manager-row-${m.id}`} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0"><UserCog className="w-5 h-5 text-blue-400" /></div>
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-slate-100 truncate">{m.first_name} {m.last_name} <span className="text-[9px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 rounded-full ml-1">Manager</span></p>
                      <p className="text-xs font-mono text-slate-400 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3 text-slate-500" /> {m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button data-testid={`manager-edit-${m.id}`} onClick={() => startEdit(m)} size="icon" variant="ghost" className="text-slate-400 hover:text-yellow-400 hover:bg-yellow-950/30"><Pencil className="w-4 h-4" /></Button>
                    <Button data-testid={`manager-delete-${m.id}`} onClick={() => remove(m)} disabled={removing === m.id} size="icon" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-950/40">{removing === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Admin;
